/**
 * ADTopologyView — Visual schema of an Active Directory deployment.
 *
 * Reads the AD topology from the backend `/api/ad-topology/<service_id>` and
 * renders a multi-site diagram inspired by classic AD design boards:
 *   ┌───────────────┐   ┌───────────────┐   ┌───────────────┐
 *   │ Sede Central  │ ↔ │ Barcelona     │ ↔ │ Valencia      │
 *   │ DCs + FSMO    │   │ DC local      │   │ DC local      │
 *   │ GPOs / GC     │   │ Subnets       │   │ Subnets       │
 *   └───────────────┘   └───────────────┘   └───────────────┘
 *
 * Editing model:
 *   - Forest / domain name + notes are inline-editable.
 *   - Qué DCs forman ESTE servicio: `dc_ids`. Si no se asigna ninguno se
 *     muestran todos los DCs de la compañía (necesario para tener a la vez un
 *     AD multisede y otro de una sola sede).
 *   - Sedes (AD sites): nombre, Location, subredes, descripción y primaria.
 *   - GPOs, trusts, enlaces de replicación y DNS guardados en
 *     Service.config_json.ad_topology y editados aquí.
 *   - DC flags (PDC, GC, FSMO roles) se marcan en la ficha de cada servidor
 *     (Organización → Active Directory) o de cada VM (pestaña Active
 *     Directory) — esta vista los muestra y avisa de conflictos.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  ServerStackIcon, GlobeAltIcon, StarIcon, ShieldCheckIcon,
  ArrowPathIcon, PlusIcon, TrashIcon, PencilSquareIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import ADServiceDcManager from './ADServiceDcManager';
import ADServiceSitesEditor from './ADServiceSitesEditor';
import ADServiceOuSection from './ADServiceOuSection';
import ADServiceGpoSection from './ADServiceGpoSection';
import ADServiceDnsSection from './ADServiceDnsSection';
import DocSmartButton from './DocSmartButton';

// ── helpers ─────────────────────────────────────────────────────────────────

const FSMO_ORDER = [
  'schema_master', 'domain_naming_master', 'pdc_emulator',
  'rid_master', 'infrastructure_master',
];

const FSMO_SHORT = {
  schema_master:         'Schema',
  domain_naming_master:  'Naming',
  pdc_emulator:          'PDC',
  rid_master:            'RID',
  infrastructure_master: 'Infra',
};

const C = {
  siteBorder:    '1px solid var(--slate-300)',
  siteHeader:    'var(--bg-card-strong, var(--bg-card))',
  dcBorder:      '1px solid rgba(59,130,246,.45)',
  dcBg:          'rgba(59,130,246,.08)',
  dcText:        'var(--text-primary)',
  primaryBorder: '2px solid #f59e0b',
  pdcBadge:      { bg: 'rgba(245,158,11,.18)',  fg: '#fbbf24', label: 'PDC ⭐' },
  gcBadge:       { bg: 'rgba(16,185,129,.18)',  fg: '#6ee7b7', label: 'GC 🌐' },
  fsmoBadge:     { bg: 'rgba(99,102,241,.18)',  fg: '#a5b4fc' },
  gpoCard:       { bg: 'rgba(139,92,246,.10)',  border: '1px solid rgba(139,92,246,.4)' },
  trustCard:     { bg: 'rgba(244,114,182,.10)', border: '1px solid rgba(244,114,182,.4)' },
};

function fsmoBadge(role) {
  return (
    <span
      key={role}
      style={{
        background: role === 'pdc_emulator' ? C.pdcBadge.bg : C.fsmoBadge.bg,
        color:      role === 'pdc_emulator' ? C.pdcBadge.fg : C.fsmoBadge.fg,
        fontSize:   '0.66rem',
        fontWeight: 700,
        borderRadius: 4,
        padding: '1px 6px',
        textTransform: 'uppercase',
        letterSpacing: '.04em',
      }}
      title={role}
    >
      {FSMO_SHORT[role] || role}
    </span>
  );
}

function DCCard({ dc, isPrimarySite }) {
  const isPdc = (dc.fsmo_roles || []).includes('pdc_emulator');
  const notDeclared = dc.is_declared_dc === false;
  return (
    <div
      style={{
        background:    C.dcBg,
        border:        notDeclared ? '1px dashed rgba(239,68,68,.6)'
                      : (isPdc ? C.primaryBorder : C.dcBorder),
        borderRadius:  6,
        padding:       '8px 10px',
        marginBottom:  8,
        color:         C.dcText,
        boxShadow:     isPrimarySite ? '0 0 0 1px rgba(245,158,11,.15)' : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <ServerStackIcon style={{ width: 14, height: 14, color: '#93c5fd' }} />
        <strong style={{ fontSize: '0.82rem' }}>{dc.hostname || dc.name}</strong>
        {dc.kind === 'vm' && (
          <span style={{ fontSize: '0.6rem', background: 'rgba(56,189,248,.16)', color: '#7dd3fc', borderRadius: 3, padding: '0 5px' }}>
            VM
          </span>
        )}
      </div>
      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: 4 }}>
        {dc.domain || '—'}
        {dc.ip_address && <> · {dc.ip_address}</>}
        {!dc.ip_address && dc.fqdn && <> · {dc.fqdn}</>}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {isPdc && (
          <span style={{
            background: C.pdcBadge.bg, color: C.pdcBadge.fg,
            fontSize: '0.66rem', fontWeight: 700, borderRadius: 4,
            padding: '1px 6px', letterSpacing: '.04em',
          }}>
            {C.pdcBadge.label}
          </span>
        )}
        {dc.is_global_catalog && (
          <span style={{
            background: C.gcBadge.bg, color: C.gcBadge.fg,
            fontSize: '0.66rem', fontWeight: 700, borderRadius: 4,
            padding: '1px 6px', letterSpacing: '.04em',
          }}>
            {C.gcBadge.label}
          </span>
        )}
        {(dc.fsmo_roles || []).filter(r => r !== 'pdc_emulator').map(fsmoBadge)}
        {notDeclared && (
          <span
            title="Asignado a este servicio, pero no está marcado como Domain Controller en su ficha"
            style={{
              background: 'rgba(239,68,68,.15)', color: '#fca5a5',
              fontSize: '0.66rem', fontWeight: 700, borderRadius: 4,
              padding: '1px 6px', letterSpacing: '.03em',
            }}
          >
            ⚠ sin marcar como DC
          </span>
        )}
      </div>
    </div>
  );
}

function SiteCard({ site, dcs }) {
  const siteDcs = (site.dc_ids || []).map(ref =>
    dcs.find(d => d.kind === ref.kind && d.id === ref.id)
  ).filter(Boolean);

  return (
    <div
      style={{
        flex: '0 0 280px',
        background: 'var(--bg-card)',
        border: site.is_primary ? '2px solid #f59e0b' : C.siteBorder,
        borderRadius: 10,
        overflow: 'hidden',
        boxShadow: site.is_primary ? '0 0 0 1px rgba(245,158,11,.12)' : 'none',
      }}
    >
      <div style={{
        padding: '8px 12px',
        background: C.siteHeader,
        borderBottom: '1px solid var(--slate-300)',
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <GlobeAltIcon style={{ width: 16, height: 16, color: site.is_primary ? '#f59e0b' : 'var(--text-muted)' }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            {site.name}
            {site.is_primary && (
              <StarIcon style={{ width: 12, height: 12, color: '#f59e0b', marginLeft: 4, display: 'inline' }} />
            )}
          </div>
          {site.subnets && site.subnets.length > 0 && (
            <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
              {site.subnets.join(', ')}
            </div>
          )}
        </div>
        <span style={{
          fontSize: '0.68rem', background: 'var(--bg-input)', color: 'var(--text-muted)',
          padding: '1px 6px', borderRadius: 4,
        }}>
          {siteDcs.length} DC{siteDcs.length === 1 ? '' : 's'}
        </span>
      </div>
      <div style={{ padding: 10, minHeight: 80 }}>
        {site.description && (
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 6 }}>
            {site.description}
          </div>
        )}
        {siteDcs.length === 0 ? (
          <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
            Sin DCs marcados en este sitio.
          </div>
        ) : (
          siteDcs.map(dc => <DCCard key={`${dc.kind}-${dc.id}`} dc={dc} isPrimarySite={site.is_primary} />)
        )}
      </div>
    </div>
  );
}

// ── main component ──────────────────────────────────────────────────────────

export default function ADTopologyView({ serviceId, companyId }) {
  const [topology, setTopology] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [saving,   setSaving]   = useState(false);

  // editable forms (mirror server data on load)
  const [editMode,  setEditMode]  = useState(false);
  const [forestName, setForestName] = useState('');
  const [domainName, setDomainName] = useState('');
  const [notes,      setNotes]      = useState('');
  const [gpos,       setGpos]       = useState([]);
  const [trusts,     setTrusts]     = useState([]);
  const [replLinks,  setReplLinks]  = useState([]);
  const [dcIds,      setDcIds]      = useState([]);
  const [sites,      setSites]      = useState([]);
  const [ous,        setOus]        = useState([]);
  const [ouPresets,  setOuPresets]  = useState([]);
  const [dns,        setDns]        = useState({ zones: [], forwarders: [], notes: '' });
  const [locations,  setLocations]  = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getADTopology(serviceId, companyId);
      setTopology(data);
      setForestName(data.forest_name || '');
      setDomainName(data.domain_name || '');
      setNotes(data.notes || '');
      setGpos(Array.isArray(data.gpos) ? data.gpos : []);
      setTrusts(Array.isArray(data.trusts) ? data.trusts : []);
      setReplLinks(Array.isArray(data.replication_links) ? data.replication_links : []);
      setDcIds(Array.isArray(data.dc_ids) ? data.dc_ids : []);
      setSites((data.sites || []).map(s => ({
        name:        s.name || '',
        location_id: s.location_id ?? null,
        subnets:     s.subnets || [],
        description: s.description || '',
        is_primary:  !!s.is_primary,
        pinned_dcs:  Array.isArray(s.pinned_dcs) ? s.pinned_dcs : [],
      })));
      setDns({
        zones:      data.dns?.zones || [],
        forwarders: data.dns?.forwarders || [],
        notes:      data.dns?.notes || '',
      });
      setOus((data.ous || []).map(o => ({
        id:          o.id,
        name:        o.name || '',
        parent_id:   o.parent_id ?? null,
        description: o.description || '',
        site:        o.site || '',
      })));
      setOuPresets(Array.isArray(data.typical_ou_presets) ? data.typical_ou_presets : []);
    } catch (e) {
      setError(e.message || 'Error cargando topología AD');
    } finally {
      setLoading(false);
    }
  }, [serviceId, companyId]);

  useEffect(() => { if (serviceId) load(); }, [serviceId, load]);

  // Locations del CMDB para vincular las sedes documentadas
  useEffect(() => {
    api.getLocations('', companyId)
      .then(r => setLocations(r.data || []))
      .catch(() => {});
  }, [companyId]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const primarySite = (sites || []).find(s => s.is_primary);
      await api.updateADTopology(serviceId, {
        forest_name: forestName,
        domain_name: domainName,
        notes,
        gpos,
        trusts,
        replication_links: replLinks,
        dc_ids: dcIds,
        sites,
        ous,
        dns,
        primary_site_location_id: primarySite?.location_id ?? null,
      }, companyId);
      setEditMode(false);
      await load();
    } catch (e) {
      setError(e.message || 'Error guardando topología AD');
    } finally {
      setSaving(false);
    }
  };

  // ── FSMO conflict detection ──
  const fsmoConflicts = useMemo(() => {
    if (!topology) return [];
    const conflicts = [];
    FSMO_ORDER.forEach(role => {
      const holders = (topology.dcs || []).filter(dc => (dc.fsmo_roles || []).includes(role));
      if (holders.length > 1) {
        conflicts.push({
          role,
          label: topology.fsmo_role_labels?.[role] || role,
          holders: holders.map(h => h.hostname || h.name),
        });
      }
    });
    return conflicts;
  }, [topology]);

  const missingFsmo = useMemo(() => {
    if (!topology) return [];
    return FSMO_ORDER.filter(role =>
      !(topology.dcs || []).some(dc => (dc.fsmo_roles || []).includes(role))
    );
  }, [topology]);

  const undeclaredCount = useMemo(
    () => (topology?.dcs || []).filter(d => d.is_declared_dc === false).length,
    [topology]
  );

  if (loading) return <div className="dv-tab-pane">Cargando topología AD…</div>;
  if (error)   return <div className="dv-tab-pane" style={{ color: '#f87171' }}>{error}</div>;
  if (!topology) return null;

  const autoScope = (topology.dc_ids || []).length === 0;

  return (
    <div className="dv-tab-pane" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: 'var(--text-muted)', letterSpacing: '.06em' }}>
            Esquema de Active Directory
          </div>
          {editMode ? (
            <div className="row g-2" style={{ marginTop: 4 }}>
              <div className="col-md-6">
                <label className="form-label">Bosque (forest)</label>
                <input className="form-control form-control-sm" value={forestName} onChange={e => setForestName(e.target.value)} />
              </div>
              <div className="col-md-6">
                <label className="form-label">Dominio raíz</label>
                <input className="form-control form-control-sm" value={domainName} onChange={e => setDomainName(e.target.value)} />
              </div>
            </div>
          ) : (
            <div>
              <h3 style={{ margin: '4px 0', color: 'var(--text-primary)' }}>{topology.forest_name}</h3>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                Dominio raíz: <strong>{topology.domain_name}</strong>
                {' · '}
                {autoScope
                  ? `${(topology.dcs || []).length} DC (todos los de la compañía)`
                  : `${(topology.dcs || []).length} DC asignado${(topology.dcs || []).length === 1 ? '' : 's'} a este servicio`}
              </div>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <DocSmartButton
            elementType="service"
            elementId={serviceId}
            elementName={topology.service_name}
            companyId={companyId}
          />
          <button className="dv-btn" type="button" onClick={load} title="Recargar">
            <ArrowPathIcon style={{ width: 14, height: 14 }} /> Recargar
          </button>
          {editMode ? (
            <>
              <button className="dv-btn" type="button" onClick={() => { setEditMode(false); load(); }} disabled={saving}>
                Cancelar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          ) : (
            <button className="dv-btn dv-btn-primary" type="button" onClick={() => setEditMode(true)}>
              <PencilSquareIcon style={{ width: 14, height: 14 }} /> Editar
            </button>
          )}
        </div>
      </div>

      {/* ── Ámbito: qué DCs forman este servicio ── */}
      {editMode ? (
        <ADServiceDcManager
          serviceId={serviceId}
          companyId={companyId}
          dcIds={dcIds}
          onChange={setDcIds}
          sites={sites}
        />
      ) : autoScope && (topology.dcs || []).length > 0 && (
        <div style={{
          fontSize: '0.78rem', color: '#fbbf24',
          background: 'rgba(245,158,11,.08)', border: '1px dashed rgba(245,158,11,.4)',
          borderRadius: 6, padding: '8px 10px',
        }}>
          Esta vista muestra <strong>todos</strong> los DC de la compañía porque el servicio no
          tiene DCs asignados. Si conviven varios AD (multisede, una sede, bosques de clientes),
          usa <em>Editar → Domain Controllers de este servicio</em> para acotarlo.
        </div>
      )}

      {undeclaredCount > 0 && (
        <div style={{
          fontSize: '0.78rem', color: '#fca5a5', display: 'flex', gap: 6, alignItems: 'flex-start',
          background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.35)',
          borderRadius: 6, padding: '8px 10px',
        }}>
          <ExclamationTriangleIcon style={{ width: 15, height: 15, flexShrink: 0, marginTop: 2 }} />
          <div>
            Hay {undeclaredCount === 1 ? 'un CI asignado' : `${undeclaredCount} CIs asignados`} a este
            servicio sin marcar como Domain Controller en su ficha: aparecen en el diagrama con el
            aviso <em>«sin marcar como DC»</em> y sin dominio, sitio, GC ni roles FSMO.
          </div>
        </div>
      )}

      {topology.domain_is_fallback && !editMode && (
        <div style={{
          fontSize: '0.78rem', color: '#fbbf24',
          background: 'rgba(245,158,11,.08)', border: '1px dashed rgba(245,158,11,.4)',
          borderRadius: 6, padding: '8px 10px',
        }}>
          El <strong>dominio raíz</strong> de este servicio está vacío, así que los nombres
          distinguidos (DN) de las OUs se calculan con el nombre interno del servicio
          (<code>{topology.domain_name}</code>). Rellénalo en <em>Editar → Dominio raíz</em>
          para que los DN sean los reales (p. ej. <code>corp.empresa.local</code>).
        </div>
      )}

      {/* ── FSMO summary ── */}
      <div style={{
        background: 'var(--bg-card)', border: '1px solid var(--slate-300)',
        borderRadius: 8, padding: 12,
      }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, marginBottom: 8, color: 'var(--text-primary)' }}>
          Roles FSMO en el bosque
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {FSMO_ORDER.map(role => {
            const holder = topology.fsmo_holders?.[role];
            return (
              <div key={role} style={{
                background: holder ? 'rgba(99,102,241,.08)' : 'rgba(239,68,68,.06)',
                border: holder ? '1px solid rgba(99,102,241,.3)' : '1px dashed rgba(239,68,68,.4)',
                borderRadius: 6, padding: '6px 10px', minWidth: 170,
              }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.04em' }}>
                  {topology.fsmo_role_labels?.[role] || role}
                </div>
                <div style={{ fontSize: '0.82rem', color: holder ? 'var(--text-primary)' : '#f87171' }}>
                  {holder ? (holder.hostname || holder.name) : '— sin asignar —'}
                </div>
              </div>
            );
          })}
        </div>
        {(fsmoConflicts.length > 0 || missingFsmo.length > 0) && (
          <div style={{ marginTop: 10, fontSize: '0.78rem' }}>
            {fsmoConflicts.map(c => (
              <div key={c.role} style={{ color: '#f87171' }}>
                ⚠ {c.label} declarado en varios DCs: {c.holders.join(', ')}
              </div>
            ))}
            {missingFsmo.length > 0 && (
              <div style={{ color: '#fbbf24' }}>
                ⚠ Sin asignar: {missingFsmo.map(r => topology.fsmo_role_labels?.[r] || r).join(', ')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Sites editor (modo edición) ── */}
      {editMode && (
        <ADServiceSitesEditor
          sites={sites}
          onChange={setSites}
          locations={locations}
          serviceDcs={topology.dcs}
        />
      )}

      {/* ── Sites grid ── */}
      <div>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, marginBottom: 8, color: 'var(--text-primary)' }}>
          Sedes y Domain Controllers
        </div>
        {(topology.sites || []).length === 0 ? (
          <div style={{ padding: 20, background: 'var(--bg-card)', borderRadius: 8, color: 'var(--text-muted)' }}>
            No hay sedes ni DCs definidos. Marca servidores como Domain Controller desde la
            pestaña <strong>Organización → Active Directory</strong> de cada servidor (o la pestaña
            <strong> Active Directory</strong> de cada VM) y asígnalos a este servicio para que
            aparezcan aquí.
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {topology.sites.map(site => (
              <SiteCard key={site.key} site={site} dcs={topology.dcs} />
            ))}
          </div>
        )}
      </div>

      {/* ── OUs ── */}
      <ADServiceOuSection
        ous={editMode ? ous : (topology.ous || [])}
        onChange={setOus}
        gpos={gpos}
        sites={sites.length ? sites : (topology.sites || [])}
        domainName={domainName || topology.domain_name}
        presets={ouPresets}
        editMode={editMode}
        domainIsFallback={!!topology.domain_is_fallback}
      />

      {/* ── GPOs ── */}
      <ADServiceGpoSection
        gpos={gpos}
        onChange={setGpos}
        editMode={editMode}
        ous={editMode ? ous : (topology.ous || [])}
        domainName={domainName || topology.domain_name}
      />

      {/* ── Trusts ── */}
      <ListSection
        title="Relaciones de confianza (trusts)"
        items={trusts}
        editMode={editMode}
        onChange={setTrusts}
        columns={[
          { key: 'from_domain', label: 'Desde dominio' },
          { key: 'to_domain',   label: 'Hacia dominio' },
          { key: 'type',        label: 'Tipo',      placeholder: 'forest / external / shortcut' },
          { key: 'direction',   label: 'Dirección', placeholder: 'one-way / two-way' },
        ]}
        cardStyle={C.trustCard}
      />

      {/* ── Replication links ── */}
      <ListSection
        title="Enlaces de replicación entre sitios"
        items={replLinks}
        editMode={editMode}
        onChange={setReplLinks}
        columns={[
          { key: 'from_site', label: 'Desde sitio' },
          { key: 'to_site',   label: 'Hacia sitio' },
          { key: 'transport', label: 'Transporte', placeholder: 'IP / SMTP' },
          { key: 'schedule',  label: 'Frecuencia', placeholder: 'cada 15 min, 24/7' },
          { key: 'cost',      label: 'Coste',      placeholder: 'ej. 100' },
        ]}
        cardStyle={{ bg: 'rgba(34,197,94,.08)', border: '1px solid rgba(34,197,94,.35)' }}
      />

      {/* ── DNS ── */}
      <ADServiceDnsSection dns={dns} onChange={setDns} editMode={editMode} />

      {/* ── Considerations / Notes ── */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--slate-300)', borderRadius: 8, padding: 12 }}>
        <div style={{ fontSize: '0.78rem', fontWeight: 700, marginBottom: 8, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <ShieldCheckIcon style={{ width: 14, height: 14 }} /> Consideraciones de diseño
        </div>
        {editMode ? (
          <textarea
            className="form-control"
            rows={6}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            placeholder="Sites/Subnets, GC, PDC Emulator, Replicación, GPOs, Seguridad…"
          />
        ) : (
          notes ? (
            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.82rem', color: 'var(--text-primary)', margin: 0 }}>
              {notes}
            </pre>
          ) : (
            <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              Sin notas. Usa el botón Editar para añadir consideraciones de diseño (sitios y
              subnets, ubicación del PDC Emulator, política de Global Catalog, esquema de
              replicación, GPOs, seguridad…).
            </div>
          )
        )}
      </div>
    </div>
  );
}

// ── editable list (GPOs / trusts / replication) ─────────────────────────────

function ListSection({ title, items, editMode, onChange, columns, cardStyle }) {
  const addItem = () => {
    const empty = {};
    columns.forEach(c => { empty[c.key] = ''; });
    onChange([...(items || []), empty]);
  };
  const removeItem = (idx) => {
    const next = (items || []).slice();
    next.splice(idx, 1);
    onChange(next);
  };
  const updateItem = (idx, key, value) => {
    const next = (items || []).slice();
    next[idx] = { ...(next[idx] || {}), [key]: value };
    onChange(next);
  };

  return (
    <div style={{
      background: cardStyle?.bg || 'var(--bg-card)',
      border: cardStyle?.border || '1px solid var(--slate-300)',
      borderRadius: 8, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)' }}>
          {title}
          <span style={{
            marginLeft: 6, fontSize: '0.7rem', background: 'var(--bg-input)',
            color: 'var(--text-muted)', borderRadius: 4, padding: '0 6px',
          }}>
            {(items || []).length}
          </span>
        </div>
        {editMode && (
          <button className="dv-btn" type="button" onClick={addItem}>
            <PlusIcon style={{ width: 14, height: 14 }} /> Añadir
          </button>
        )}
      </div>

      {(items || []).length === 0 ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Sin entradas.
        </div>
      ) : editMode ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(items || []).map((it, idx) => (
            <div key={idx} className="row g-2" style={{ alignItems: 'flex-end' }}>
              {columns.map(col => (
                <div key={col.key} className="col">
                  <label className="form-label" style={{ fontSize: '0.7rem' }}>{col.label}</label>
                  <input
                    className="form-control form-control-sm"
                    placeholder={col.placeholder || ''}
                    value={it[col.key] || ''}
                    onChange={e => updateItem(idx, col.key, e.target.value)}
                  />
                </div>
              ))}
              <div className="col-auto">
                <button className="dv-btn" type="button" onClick={() => removeItem(idx)} title="Eliminar">
                  <TrashIcon style={{ width: 14, height: 14 }} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead>
              <tr>
                {columns.map(c => (
                  <th key={c.key} style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--text-muted)', borderBottom: '1px solid var(--slate-300)' }}>
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(items || []).map((it, idx) => (
                <tr key={idx}>
                  {columns.map(c => (
                    <td key={c.key} style={{ padding: '4px 8px', borderBottom: '1px solid var(--slate-200)', color: 'var(--text-primary)' }}>
                      {it[c.key] || '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
